import invert from 'lodash/invert';
import bookingFlowLang from '../../bookingFlow.lang';

/** A mapping of finite state machine states and their corresponding paths.
 * These are appended to the base url as matched in the router.
 *
 * ie Navigating to the RfvEntry state sets the url to `/book/bf/rfv-entry` */
export const StatesToRoutes = {
  RfvEntry: 'rfv-entry',
  EnterInfoOrLogin: 'enter-info-or-login',
  NumberLookup: 'number-lookup',
  VerifyNumber: 'verify-number',
  NewAccountWho: 'new-account-who',
  NewAccountEnterInfo: 'new-account-enter-info',
  HomeAddress: 'home-address',
  SelectPatient: 'select-patient',
  NewFamilyMember: 'new-family-member',
  FamilyMemberDetails: 'family-member-details',
  PaymentMethod: 'payment-method',
  PaymentCollection: 'payment-collection',
  AddInsurance: 'add-insurance',
  PhotoIdUpload: 'photo-id-upload',
  Finalize: 'finalize',
  SelectATime: 'select-a-time',
} as const;

export const BOOKING_FLOW_ENTRY = `/book/bf/${StatesToRoutes.RfvEntry}`;

/** String union of all state names */
export type StateName = keyof typeof StatesToRoutes;

/** Maps state names to the browser webpage title to show with <Helmet>. Each value is a
 * key in bookingFlow.lang.ts for the correct title string under the 'stepTitles' prop */
export const StatesToPageTitles: Record<StateName, keyof typeof bookingFlowLang['stepTitles']> = {
  RfvEntry: 'reasonForVisit',
  EnterInfoOrLogin: 'patientInformation',
  NumberLookup: 'logIn',
  VerifyNumber: 'verifyPhoneNumber',
  NewAccountWho: 'selectPatient',
  NewAccountEnterInfo: 'patientInformation',
  HomeAddress: 'patientInformation',
  SelectPatient: 'selectPatient',
  NewFamilyMember: 'addAFamilyMember',
  FamilyMemberDetails: 'patientInformation',
  PaymentMethod: 'patientInformation',
  PaymentCollection: 'patientInformation',
  AddInsurance: 'patientInformation',
  PhotoIdUpload: 'patientInformation',
  Finalize: 'finalize',
  SelectATime: 'patientInformation',
};

/** String union of all routes */
export type Route = typeof StatesToRoutes[keyof typeof StatesToRoutes];

/** Actions that transition between states in the Booking Flow.
 * Many pages fire only FORM_SUBMITTED when their Submit button is pressed. But,
 * pages with multiple options will need to have multiple events defined here,
 *
 * All pages support BACK & EXIT via the navbar UI. */
export type BookingFlowEvent =
  | 'FORM_SUBMITTED'
  | 'WRONG_NUMBER'
  | 'ADD_NEW_FAMILY_MEMBER'
  | 'CHOOSE_INSURANCE'
  | 'CHOOSE_SELF_PAY'
  | 'SELECT_A_TIME'
  | 'SKIP'
  | 'BACK'
  | 'EXIT'
  | 'LOGIN';

/** The state to navigate to when a transition is followed.
 * 'GO_BACK' and 'EXIT_BOOKING_FLOW' are special transitions that don't target an
 * actual "state", but instead tells the transition handling logic to fire special base behavior:
 * - LOGIN_STATE_CHANGE: Triggers a potential navigation change based on Booking Flow state's required login state.
 * - GO_BACK: Navigate back in the browser via history.goBack()
 * - EXIT_BOOKING_FLOW: Exit the booking flow to the location from which we had
 * entered. */
export type TransitionTarget = StateName | 'GO_BACK' | 'EXIT_BOOKING_FLOW' | 'LOGIN_STATE_CHANGE';

export type ConditionalTransition<T extends TransitionDefinition> = {
  cond: (ctx: TransitionContextType) => boolean;
  target: T;
  /** Documentation string used when generating a flowchart of the Booking Flow */
  label?: string;
};

// The 0th item is a label for the transition used when generating a flowchart of the Booking Flow
// Note that this is an interface: as of Typescript 4.1.6 this is not allowed to reference
// itself if defined as a type alias
export type ConditionalTransitions = readonly [
  string,
  ...ConditionalTransition<TransitionDefinition>[]
];

/** A transition from one state to another state that is allowed in the state
 * machine. */
export type TransitionDefinition =
  /** Non-conditional transitions are always followed */
  | TransitionTarget
  /** One or more conditional transitions; each are tried in order and the first to be satisfied
   * is followed while the rest are ignored */
  | ConditionalTransitions;

/** Is the transition just a state name? */
export function isSimpleTransition(td: TransitionDefinition): td is TransitionTarget {
  return typeof td === 'string';
}

/** Is the conditional transition just targeting a state name? */
export function isSimpleConditionalTransition(
  ct: ConditionalTransition<TransitionDefinition>
): ct is ConditionalTransition<TransitionTarget> {
  return typeof ct.target === 'string';
}

/** Is the conditional transition targeting another transition? This means the target transition will be evaluated
 * if this transition's condition is satisfied */
export function isChainedConditionalTransition(
  ct: ConditionalTransition<TransitionDefinition>
): ct is ConditionalTransition<ConditionalTransitions> {
  return typeof ct.target !== 'string';
}

/** Util type for reversing a TypeScript record so that keys are values and values are keys */
type Reverser<T extends Record<PropertyKey, PropertyKey>> = {
  [P in keyof T as T[P]]: P;
};

/** Reverse mapping of page routes to state names */
export const RoutesToStates = invert(StatesToRoutes) as Reverser<typeof StatesToRoutes>;

/** Defines the states and transitions for a finite state machine comprising a
 * Booking Flow. */
export type FlowConfig<
  S extends string | number | symbol,
  E extends string | number | symbol
> = Record<
  S,
  {
    /* "on" transitions are defined under "on" to keep parity with the config used
     * by xstate; We can remove this extra poperty and define transitions on the
     * root object if we decide to keep this approach and not use xstate. */
    on: Partial<{
      [key in E]: TransitionDefinition;
    }>;
    requiresLogin: boolean;
  }
>;

export type SelectedPaymentMethod = 'deciding later' | 'insurance' | 'self pay' | undefined;

/** The values provided to each transition condition for determining if the transition should be followed. */
export type TransitionContextType = {
  isLoggedIn: boolean;
  isNewAccount: boolean;
  isPhotoIdUploadEnabled: boolean;
  isInsuranceDisabled: boolean;
  paymentMethodOptions: 'self-pay only' | 'insurance only' | 'self-pay or insurance';
  paymentMethodCollectionEnabled: boolean;
  /** The type of payment method being used for this booking. This context prop is unique
   * in that it gets updated when we actually make the payment method choice in the Payment
   * Method page. */
  paymentMethodPicked: SelectedPaymentMethod;
  /** A Setter for paymentMethodPicked. */
  setPaymentMethodPicked: (method: SelectedPaymentMethod) => void;
  requiresHomeAddress: boolean;
  isCreatingProfile: boolean;
  skippedLogin: boolean;
};
